<template>
  <div class="mini-filter-bar" :class="{ collapsed: isCollapsed }">
    <!-- Свернутое состояние -->
    <div v-if="isCollapsed" class="collapsed-panel">
      <button class="toggle-btn" @click="isCollapsed = false">▶</button>
    </div>

    <!-- Развернутое состояние -->
    <div v-else class="filter-container">
      <button class="toggle-btn" @click="isCollapsed = true">◀</button>
      <input
        v-model="local.catalog_id"
        type="text"
        class="catalog-input"
        :placeholder="$t('mini_filter_bar.catalog_id_placeholder')"
        @keyup.enter="apply"
      />
      <!-- Локация -->
      <select v-model="local.country_id" @change="onCountryChange">
        <option value="">{{ $t('mini_filter_bar.country') }}</option>
        <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.name }}</option>
      </select>

      <select v-model="local.region_id" @change="onRegionChange" :disabled="!local.country_id">
        <option value="">{{ $t('mini_filter_bar.region') }}</option>
        <option v-for="r in regionsForCountry" :key="r.id" :value="r.id">{{ r.name }}</option>
      </select>

      <select v-model="local.city_id" @change="onCityChange" :disabled="!local.region_id">
        <option value="">{{ $t('mini_filter_bar.city') }}</option>
        <option v-for="c in citiesForRegion" :key="c.id" :value="c.id">{{ c.name }}</option>
      </select>

      <select v-model="local.district_id" :disabled="!local.city_id">
        <option value="">{{ $t('mini_filter_bar.district') }}</option>
        <option v-for="d in districtsForCity" :key="d.id" :value="d.id">{{ d.name }}</option>
      </select>

      <!-- Категории -->
      <select v-model="local.category" @change="onCategoryChange">
        <option value="">{{ $t('mini_filter_bar.category') }}</option>
        <option v-for="cat in categories" :key="cat.key" :value="cat.key">{{ cat.label }}</option>
      </select>

      <select v-model="local.type" @change="onTypeChange" :disabled="!local.category">
        <option value="">{{ $t('mini_filter_bar.type') }}</option>
        <option v-for="t in typesForCategory" :key="t.key" :value="t.key">{{ t.label }}</option>
      </select>

      <select v-model="local.subcategory" :disabled="!local.type">
        <option value="">{{ $t('mini_filter_bar.subtype') }}</option>
        <option v-for="sub in subcategoriesForType" :key="sub.key" :value="sub.key">{{ sub.label }}</option>
      </select>

      <!-- 🔹 Новый селект "Тип сделки" -->
      <select v-model="local.purpose">
        <option value="">{{ $t('mini_filter_bar.purpose') }}</option>
        <option v-for="p in purposeOptions" :key="p.value" :value="p.value">{{ p.label }}</option>
      </select>

      <!-- Кнопки -->
      <button @click="apply" class="apply-btn">{{ $t('mini_filter_bar.apply') }}</button>
      <button @click="reset" class="reset-btn">{{ $t('mini_filter_bar.reset') }}</button>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useFilterStore } from '@/stores/filterStore'

const { t, locale } = useI18n()
const emit = defineEmits(['update'])
const filterStore = useFilterStore()
const { catalog_id, country_id, region_id, city_id, district_id, category, type, subcategory, purpose } = storeToRefs(filterStore)

const local = ref({
  country_id: '',
  region_id: '',
  city_id: '',
  district_id: '',
  category: '',
  type: '',
  subcategory: '',
  purpose: '',
  catalog_id: '' 
})

const countries = ref([])
const categories = ref([])
const isCollapsed = ref(false)

// ✅ динамически локализованный список целей сделки
const purposeOptions = computed(() => [
  { label: t('mini_filter_bar.purpose_options.sale'), value: 'sale' },
  { label: t('mini_filter_bar.purpose_options.rental'), value: 'rental' },
  { label: t('mini_filter_bar.purpose_options.daily_rent'), value: 'daily_rent' },
  // { label: t('mini_filter_bar.purpose_options.investment'), value: 'investment' },
  //{ label: t('mini_filter_bar.purpose_options.holiday'), value: 'holiday' },
  //{ label: t('mini_filter_bar.purpose_options.commercial_use'), value: 'commercial_use' },
  //{ label: t('mini_filter_bar.purpose_options.residence'), value: 'residence' }
])

async function loadFilters() {
  const currentLocale = (locale.value || 'ru').split('-')[0]
  try {
    const [locResp, catResp] = await Promise.all([
      fetch('/api/v1/locations', {
        cache: 'no-store',
        headers: { 'X-Locale': currentLocale }
      }),
      fetch('/api/v1/property_types', {
        cache: 'no-store',
        headers: { 'X-Locale': currentLocale }
      })
    ])
    const locData = await locResp.json()
    const catData = await catResp.json()
    countries.value = locData?.countries || []
    categories.value = Array.isArray(catData) ? catData : (catData?.categories || [])
  } catch (e) {
    console.error('Ошибка загрузки фильтров', e)
  }
}

onMounted(() => {
  if (window.innerWidth <= 768) isCollapsed.value = true

  filterStore.hydrate()
  local.value = {
    country_id: country_id.value || '',
    region_id: region_id.value || '',
    city_id: city_id.value || '',
    district_id: district_id.value || '',
    category: category.value || '',
    type: type.value || '',
    subcategory: subcategory.value || '',
    purpose: purpose.value || ''
  }
  loadFilters()
})

watch(locale, () => {
  loadFilters()
})

// --- вычисляемые списки ---
const regionsForCountry = computed(() => {
  const c = countries.value.find(x => x.id === local.value.country_id)
  return c?.regions || []
})
const citiesForRegion = computed(() => {
  const r = regionsForCountry.value.find(x => x.id === local.value.region_id)
  return r?.cities || []
})
const districtsForCity = computed(() => {
  const c = citiesForRegion.value.find(x => x.id === local.value.city_id)
  return c?.districts || []
})
const typesForCategory = computed(() => {
  const cat = categories.value.find(x => x.key === local.value.category)
  return cat?.types || []
})
const subcategoriesForType = computed(() => {
  const type = typesForCategory.value.find(x => x.key === local.value.type)
  return type?.subtypes || []
})

// --- сброс зависимых полей ---
function onCountryChange() {
  local.value.region_id = ''
  local.value.city_id = ''
  local.value.district_id = ''
}
function onRegionChange() {
  local.value.city_id = ''
  local.value.district_id = ''
}
function onCityChange() {
  local.value.district_id = ''
}
function onCategoryChange() {
  local.value.type = ''
  local.value.subcategory = ''
}
function onTypeChange() {
  local.value.subcategory = ''
}

// --- применение фильтра ---
function apply() {
  console.log('🟢 apply() called, local.value =', JSON.stringify(local.value))
  filterStore.setFilters({ ...local.value })
  emit('update', {
    country_id: local.value.country_id,
    region_id: local.value.region_id,
    city_id: local.value.city_id,
    district_id: local.value.district_id,
    main_category: local.value.category,
    property_type: local.value.type,
    property_subtype: local.value.subcategory,
    purpose: local.value.purpose,
    catalog_id: local.value.catalog_id
  })
}

// --- сброс ---
function reset() {
  filterStore.reset()
  local.value = {
    country_id: '',
    region_id: '',
    city_id: '',
    district_id: '',
    category: '',
    type: '',
    subcategory: '',
    purpose: '',
    catalog_id: ''
  }
  emit('update', { ...local.value })
}
</script>


<style scoped>
.mini-filter-bar {
  background-color: #1351a8;
  position: sticky;
  top: 50px;
  z-index: 1200;
  display: flex;
  flex-wrap: wrap;
  padding: 6px;
  transition: all 0.3s ease;
}

/* Свернутое состояние */
.mini-filter-bar.collapsed {
  width: auto;
  height: auto;
  padding: 0;
  background: transparent;
}

.collapsed-panel {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
}

.toggle-btn {
  background: #1351a8;
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 60%;
  cursor: default;
  font-size: 19px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
}

/* 📱 Мобильная версия */
@media (max-width: 768px) {
  .collapsed-panel {
    position: fixed;
    top: 70px;
    left: 10px;
    z-index: 1300;
  }

  .filter-container {
    position: fixed;
    top: 60px;
    left: 0;
    width: 100%;
    background: #1351a8;
    padding: 10px;
    overflow-y: auto;
    max-height: 80vh;
  }
}

/* 🎯 Стиль селектов */
select {
  padding: 6px 8px;
  border-radius: 4px;
  border: none;
  background: #2c2c2c;
  color: #fff;
  min-width: 120px;
  margin-right: 5px;
}

select:disabled {
  opacity: 0.5;
}

/* Кнопки */
.apply-btn {
  background: rgb(61, 63, 70);
  color: #fff;
  border: none;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.reset-btn {
  background: #555;
  color: #fff;
  border: none;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.catalog-input {
  padding: 6px 8px;
  border-radius: 4px;
  border: none;
  background: #fff;
  color: #000;
  min-width: 180px;
  margin-right: 6px;
  font-weight: 600;
}

</style>
